/**
 * Operator Payment Reports - Admin View
 * 
 * Generates, views, and locks payment reports for operators
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { getJson, apiFetch } from '@/lib/http';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronDown,
  ChevronRight,
  Lock,
  Unlock,
  FileText,
  Download,
  DollarSign,
  Calendar,
  User,
  AlertCircle,
  CheckCircle,
  Clock,
  History,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Mock data types
interface PaymentReport {
  id: number;
  cycleId: number;
  cycleName: string;
  operatorId: number;
  operatorName: string;
  status: 'draft' | 'locked' | 'unlocked';
  generatedAt: string;
  generatedBy: string;
  lockedAt?: string;
  lockedBy?: string;
  unlockedAt?: string;
  totalPayment: number;
  reportData: ReportData;
}

interface ReportData {
  stores: StoreSummary[];
}

interface StoreSummary {
  storeId: number;
  storeName: string;
  campaignCount: number; // Number of unique campaigns at this store
  storeTotal: number;
  tasks: TaskSummary[];
}

interface TaskSummary {
  taskId: number;
  taskName: string;
  ruleName: string;
  ruleApplied: string;
  effectiveRate: number;
  assignmentCount: number;
  campaignCount: number; // Number of campaigns with this task at this store
  subtotal: number;
  assignments: AssignmentDetail[];
}

interface AssignmentDetail {
  assignmentId: number;
  campaignId: number;
  campaignName: string;
  packName?: string;
  quantity: number;
  payment: number;
}

// Mock data - commented out since we use API data
/*
const mockReport: PaymentReport = {
  id: 1,
  cycleId: 17,
  cycleName: 'Cycle 17 - Oct 2025',
  operatorId: 10,
  operatorName: 'Stephen Collard',
  status: 'draft',
  generatedAt: '2025-10-24T13:00:00Z',
  generatedBy: 'Admin User',
  totalPayment: 485.50,
  reportData: {
    stores: [
      {
        campaignId: 355,
        campaignName: 'Stella Artois Q4',
        campaignTotal: 165.00,
        tasks: [
          {
            taskId: 1,
            taskName: 'SV Standard Posting',
            ruleName: '3 Standard Campaigns Per Store Per Cycle',
            ruleApplied: '€11.00 per task (3+ campaigns, any stores)',
            effectiveRate: 11.00,
            effectiveFrom: '2025-10-24',
            assignmentCount: 15,
            storeCount: 15,
            subtotal: 165.00,
            assignments: [
              { assignmentId: 1001, storeId: 45, storeName: 'SV Hacketstown', quantity: 1, payment: 11.00 },
              { assignmentId: 1002, storeId: 46, storeName: 'SV Balally', quantity: 1, payment: 11.00 },
              { assignmentId: 1003, storeId: 47, storeName: 'SV Walkinstown', quantity: 1, payment: 11.00 },
              // ... more assignments
            ]
          }
        ]
      },
      {
        campaignId: 356,
        campaignName: 'Smirnoff Vodka Winter',
        campaignTotal: 165.00,
        tasks: [
          {
            taskId: 1,
            taskName: 'SV Standard Posting',
            ruleName: '3 Standard Campaigns Per Store Per Cycle',
            ruleApplied: '€11.00 per task (3+ campaigns, any stores)',
            effectiveRate: 11.00,
            effectiveFrom: '2025-10-24',
            assignmentCount: 15,
            storeCount: 15,
            subtotal: 165.00,
            assignments: [
              { assignmentId: 2001, storeId: 45, storeName: 'SV Hacketstown', quantity: 1, payment: 11.00 },
              { assignmentId: 2002, storeId: 46, storeName: 'SV Balally', quantity: 1, payment: 11.00 },
              // ... more assignments
            ]
          }
        ]
      },
      {
        campaignId: 357,
        campaignName: 'Birdseye Prawns',
        campaignTotal: 155.50,
        tasks: [
          {
            taskId: 1,
            taskName: 'SV Standard Posting',
            ruleName: '3 Standard Campaigns Per Store Per Cycle',
            ruleApplied: '€11.00 per task (3+ campaigns, any stores)',
            effectiveRate: 11.00,
            effectiveFrom: '2025-10-24',
            assignmentCount: 14,
            storeCount: 14,
            subtotal: 154.00,
            assignments: [
              { assignmentId: 3001, storeId: 45, storeName: 'SV Hacketstown', quantity: 1, payment: 11.00 },
              // ... more assignments
            ]
          },
          {
            taskId: 8,
            taskName: 'Install PopAds',
            ruleName: 'Install PopAds (Custom Rate)',
            ruleApplied: '€0.49 per task (custom operator rate)',
            effectiveRate: 0.49,
            effectiveFrom: '2025-10-24',
            assignmentCount: 3,
            storeCount: 3,
            subtotal: 1.47,
            assignments: [
              { assignmentId: 3100, storeId: 45, storeName: 'SV Hacketstown', quantity: 1, payment: 0.49 },
              // ... more assignments
            ]
          }
        ]
      }
    ]
  }
};
*/

const AdminOperatorPaymentReports: React.FC = () => {
  const { toast } = useToast();
  const [selectedRetailer, setSelectedRetailer] = useState<string>('');
  const [selectedCycle, setSelectedCycle] = useState<string>('');
  const [selectedOperator, setSelectedOperator] = useState<string>('');
  const [report, setReport] = useState<PaymentReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedStores, setExpandedStores] = useState<Set<number>>(new Set());
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Fetch retailers
  const { data: retailers = [] } = useQuery({
    queryKey: ['/api/retailers'],
    queryFn: async () => {
      const data = await getJson('/api/retailers') as any[];
      // Sort by name
      return data.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }
  });

  // Fetch cycles filtered by selected retailer
  const { data: cycles = [] } = useQuery({
    queryKey: ['/api/booking-cycles', selectedRetailer],
    queryFn: async () => {
      const url = `/api/booking-cycles?retailerId=${selectedRetailer}`;
      const data = await getJson(url) as any[];
      // Sort by ID descending (most recent first)
      return data.sort((a: any, b: any) => b.id - a.id);
    },
    enabled: !!selectedRetailer, // Only fetch when retailer is selected
  });

  // Fetch operators
  const { data: operators = [] } = useQuery({
    queryKey: ['/api/users/operators'],
    queryFn: async () => await getJson('/api/users/operators') as any[]
  });

  // Reset cycle when retailer changes
  const handleRetailerChange = (retailerId: string) => {
    setSelectedRetailer(retailerId);
    setSelectedCycle(''); // Reset cycle selection when retailer changes
  };

  const toggleStore = (storeId: number) => {
    const newExpanded = new Set(expandedStores);
    if (newExpanded.has(storeId)) {
      newExpanded.delete(storeId);
    } else {
      newExpanded.add(storeId);
    }
    setExpandedStores(newExpanded);
  };

  const toggleTask = (key: string) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedTasks(newExpanded);
  };

  const generateReport = async () => {
    try {
      setLoading(true);

      const response = await apiFetch('/api/payment-reports/generate', {
        method: 'POST',
        body: JSON.stringify({
          operatorId: parseInt(selectedOperator),
          cycleId: parseInt(selectedCycle),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate report: ${errorText}`);
      }

      const data = await response.json();

      setReport({
        id: data.id,
        cycleId: data.cycleId,
        cycleName: data.cycleName,
        operatorId: data.operatorId,
        operatorName: data.operatorName,
        status: data.status || 'draft',
        generatedAt: data.generatedAt,
        generatedBy: 'Admin', // Will be populated from backend
        totalPayment: data.totalPayment,
        reportData: {
          stores: data.stores.map((store: any) => ({
            storeId: store.storeId,
            storeName: store.storeName,
            campaignCount: store.campaignCount,
            storeTotal: store.storeTotal,
            tasks: store.tasks.map((task: any) => ({
              taskId: task.taskId,
              taskName: task.taskName,
              ruleName: task.ruleName,
              ruleApplied: task.ruleApplied,
              effectiveRate: task.effectiveRate,
              assignmentCount: task.assignmentCount,
              campaignCount: task.campaignCount,
              subtotal: task.subtotal,
              assignments: task.assignments.map((a: any) => ({
                assignmentId: a.assignmentId,
                campaignId: a.campaignId,
                campaignName: a.campaignName,
                quantity: a.quantity,
                payment: a.payment,
              })),
            })),
          })),
        },
      });

      toast({
        title: 'Success',
        description: 'Payment report generated successfully',
      });
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate payment report',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const lockReport = async () => {
    if (!report) return;

    try {
      const response = await apiFetch(`/api/payment-reports/${report.id}/lock`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to lock report: ${errorText}`);
      }

      setReport({
        ...report,
        status: 'locked',
        lockedAt: new Date().toISOString(),
        lockedBy: 'Admin User'
      });

      toast({
        title: 'Success',
        description: 'Payment report locked successfully',
      });
    } catch (error) {
      console.error('Error locking report:', error);
      toast({
        title: 'Error',
        description: 'Failed to lock payment report',
        variant: 'destructive',
      });
    }
  };

  const unlockReport = async () => {
    if (!report || !unlockReason.trim()) return;

    try {
      const response = await apiFetch(`/api/payment-reports/${report.id}/unlock`, {
        method: 'POST',
        body: JSON.stringify({ reason: unlockReason }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to unlock report: ${errorText}`);
      }

      setReport({
        ...report,
        status: 'unlocked',
        unlockedAt: new Date().toISOString()
      });

      setShowUnlockDialog(false);
      setUnlockReason('');

      toast({
        title: 'Success',
        description: 'Payment report unlocked successfully',
      });
    } catch (error) {
      console.error('Error unlocking report:', error);
      toast({
        title: 'Error',
        description: 'Failed to unlock payment report',
        variant: 'destructive',
      });
    }
  };

  const exportReport = (format: 'pdf' | 'excel' | 'csv') => {
    if (!report) return;

    // TODO: Implement export functionality
    window.open(`/api/payment-reports/${report.id}/export?format=${format}`, '_blank');

    toast({
      title: 'Export Started',
      description: `Exporting report as ${format.toUpperCase()}`,
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; icon: any; label: string }> = {
      draft: { variant: 'secondary', icon: Clock, label: 'Draft' },
      locked: { variant: 'default', icon: Lock, label: 'Locked' },
      unlocked: { variant: 'destructive', icon: Unlock, label: 'Unlocked' },
    };
    const config = variants[status] || variants.draft;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        icon={DollarSign}
        title="Payment Reports"
        description="Generate and manage operator payment reports"
      >
        <Button variant="outline" onClick={() => setShowAuditLog(true)}>
          <History className="h-4 w-4 mr-2" />
          Audit Log
        </Button>
      </PageHeader>

      {/* Selection Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Report Parameters</CardTitle>
          <CardDescription>Select retailer, cycle, and operator to generate payment report</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="retailer">Retailer</Label>
              <Select value={selectedRetailer} onValueChange={handleRetailerChange}>
                <SelectTrigger id="retailer">
                  <SelectValue placeholder="Select retailer" />
                </SelectTrigger>
                <SelectContent>
                  {retailers.map((retailer: any) => (
                    <SelectItem key={retailer.id} value={String(retailer.id)}>
                      {retailer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cycle">Cycle</Label>
              <Select value={selectedCycle} onValueChange={setSelectedCycle} disabled={!selectedRetailer}>
                <SelectTrigger id="cycle">
                  <SelectValue placeholder={selectedRetailer ? "Select cycle" : "Select retailer first"} />
                </SelectTrigger>
                <SelectContent>
                  {cycles.map((cycle: any) => (
                    <SelectItem key={cycle.id} value={String(cycle.id)}>
                      {cycle.name} / Cycle {cycle.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="operator">Operator</Label>
              <Select value={selectedOperator} onValueChange={setSelectedOperator}>
                <SelectTrigger id="operator">
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((operator: any) => (
                    <SelectItem key={operator.id} value={String(operator.id)}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                onClick={generateReport}
                disabled={loading || !selectedRetailer || !selectedCycle || !selectedOperator}
                className="w-full"
              >
                <FileText className="h-4 w-4 mr-2" />
                {loading ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Display */}
      {report && (
        <>
          {/* Report Header */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CardTitle>Payment Report #{report.id}</CardTitle>
                    {getStatusBadge(report.status)}
                  </div>
                  <CardDescription>
                    <div className="flex flex-col gap-1 mt-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{report.operatorName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{report.cycleName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>Generated: {new Date(report.generatedAt).toLocaleString()} by {report.generatedBy}</span>
                      </div>
                      {report.lockedAt && (
                        <div className="flex items-center gap-2">
                          <Lock className="h-4 w-4" />
                          <span>Locked: {new Date(report.lockedAt).toLocaleString()} by {report.lockedBy}</span>
                        </div>
                      )}
                    </div>
                  </CardDescription>
                </div>

                <div className="flex flex-col items-end gap-3">
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Total Payment</div>
                    <div className="text-3xl font-bold text-green-600">
                      €{report.totalPayment.toFixed(2)}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {report.status === 'draft' && (
                      <Button onClick={lockReport} variant="default">
                        <Lock className="h-4 w-4 mr-2" />
                        Lock Report
                      </Button>
                    )}
                    {report.status === 'locked' && (
                      <Button onClick={() => setShowUnlockDialog(true)} variant="destructive">
                        <Unlock className="h-4 w-4 mr-2" />
                        Unlock Report
                      </Button>
                    )}
                    <Button onClick={() => exportReport('pdf')} variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Export PDF
                    </Button>
                    <Button onClick={() => exportReport('excel')} variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Export Excel
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Status Alert */}
          {report.status === 'locked' && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                This report is locked and immutable. Payment calculations are finalized. To make changes, you must unlock the report with a reason.
              </AlertDescription>
            </Alert>
          )}

          {report.status === 'draft' && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This is a draft report. You can regenerate it at any time. Lock it when payments are finalized.
              </AlertDescription>
            </Alert>
          )}

          {/* Store Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Breakdown</CardTitle>
              <CardDescription>
                Detailed breakdown by store, task, and campaign. Payment rates are calculated based on the number of campaigns per unique store (not stores per campaign).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {report.reportData.stores.map((store) => (
                <div key={store.storeId} className="border rounded-lg">
                  {/* Store Header */}
                  <button
                    onClick={() => toggleStore(store.storeId)}
                    className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {expandedStores.has(store.storeId) ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronRight className="h-5 w-5" />
                      )}
                      <div className="text-left">
                        <div className="font-semibold">{store.storeName}</div>
                        <div className="text-sm text-muted-foreground">
                          {store.campaignCount} campaign{store.campaignCount !== 1 ? 's' : ''} • {store.tasks.length} task{store.tasks.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="font-bold text-lg">€{store.storeTotal.toFixed(2)}</span>
                    </div>
                  </button>

                  {/* Store Tasks */}
                  {expandedStores.has(store.storeId) && (
                    <div className="border-t bg-muted/20">
                      {store.tasks.map((task) => {
                        const taskKey = `${store.storeId}-${task.taskId}`;
                        return (
                          <div key={taskKey} className="border-b last:border-b-0">
                            {/* Task Header */}
                            <button
                              onClick={() => toggleTask(taskKey)}
                              className="w-full p-4 pl-12 flex items-center justify-between hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                {expandedTasks.has(taskKey) ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                                <div className="text-left">
                                  <div className="font-medium">{task.taskName}</div>
                                  <div className="text-sm text-muted-foreground">
                                    Rule: {task.ruleName}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {task.ruleApplied} • {task.assignmentCount} assignments • {task.campaignCount} campaign{task.campaignCount !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold">€{task.subtotal.toFixed(2)}</div>
                                <div className="text-xs text-muted-foreground">
                                  @ €{task.effectiveRate.toFixed(2)}/task
                                </div>
                              </div>
                            </button>

                            {/* Task Assignments */}
                            {expandedTasks.has(taskKey) && (
                              <div className="bg-background">
                                <table className="w-full">
                                  <thead className="bg-muted/50">
                                    <tr>
                                      <th className="text-left p-3 pl-20 text-sm font-medium">Campaign</th>
                                      <th className="text-left p-3 text-sm font-medium">Assignment ID</th>
                                      <th className="text-right p-3 text-sm font-medium">Payment</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                      {task.assignments.map((assignment) => (
                                      <tr key={assignment.assignmentId} className="border-t">
                                        <td className="p-3 pl-20 text-sm">
                                          <div className="font-medium">{assignment.campaignName}</div>
                                        </td>
                                        <td className="p-3 text-sm text-muted-foreground font-mono">
                                          #{assignment.assignmentId}
                                        </td>
                                        <td className="p-3 text-right text-sm font-medium">
                                          €{assignment.payment.toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}

              {/* Grand Total */}
              <div className="border-t-2 pt-4 flex justify-between items-center">
                <div className="text-lg font-semibold">Grand Total</div>
                <div className="text-2xl font-bold text-green-600">
                  €{report.totalPayment.toFixed(2)}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Unlock Dialog */}
      <Dialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle>Unlock Report</DialogTitle>
            <DialogDescription>
              This will unlock the report and allow regeneration. Please provide a reason for unlocking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Unlocking *</Label>
              <Textarea
                id="reason"
                placeholder="e.g., Incorrect payment calculation for Store X, need to apply updated rule"
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                rows={4}
              />
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This action will be logged in the audit trail. The current report data will be preserved.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlockDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={unlockReport}
              disabled={!unlockReason.trim()}
            >
              <Unlock className="h-4 w-4 mr-2" />
              Unlock Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOperatorPaymentReports;
